import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The My entries filters, written to the URL (spec 2026-09-11 §5.3) so a
 * filtered list can be shared, survives reload, and Back leaves the page
 * rather than replaying every chip. Keys: `q` (search), `status` (one chip),
 * `week` (Monday, Y-m-d — the server's `week_start`), `employee` (id, for
 * approvers). `tab` and anything else on the URL is left alone.
 */
export interface TimecardListFilters {
  q: string;
  status: string;
  week: string;
  employee: string;
}

const KEYS: (keyof TimecardListFilters)[] = ['q', 'status', 'week', 'employee'];

/** The Monday (ISO weekday 1) of the week holding `dateISO`, as Y-m-d; '' for an unparseable value. */
export function mondayOf(dateISO: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateISO);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return '';
  const shift = (d.getDay() + 6) % 7; // Sunday → 6
  d.setDate(d.getDate() - shift);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `weekISO` ± n weeks, Y-m-d. */
export function shiftWeek(weekISO: string, weeks: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(weekISO);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + weeks * 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function useTimecardListFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo<TimecardListFilters>(() => ({
    q: params.get('q') ?? '',
    status: params.get('status') ?? '',
    week: params.get('week') ?? '',
    employee: params.get('employee') ?? '',
  }), [params]);

  const set = useCallback((patch: Partial<TimecardListFilters>) => {
    const next = new URLSearchParams(params);
    for (const k of KEYS) {
      if (!(k in patch)) continue;
      const v = patch[k] ?? '';
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  }, [params, setParams]);

  const clear = useCallback(() => set({ q: '', status: '', week: '', employee: '' }), [set]);

  const active = KEYS.filter((k) => filters[k] !== '').length;
  return { filters, set, clear, active };
}
